/**
 * Turns an incoming link into an app path for getScreenFromPath:
 * - Universal / App Links: https://connect.dislix.com/visit/123/execute
 * - App scheme: dislix://visit/123/execute (sendifier:// for older iOS installs)
 * Returns null for anything that isn't an app page (OAuth redirects, other
 * hosts, the bare home page).
 */
const APP_LINK_HOSTS = ['connect.dislix.com'];
const APP_SCHEMES = ['dislix', 'sendifier'];
// Handled by auth sessions / tooling, never routed as screens.
const IGNORED_PATH_PREFIXES = ['/oauthredirect', '/x-twitter-callback', '/billing-return', '/expo-development-client', '/--/'];

// Order matters: the first present key becomes navigateTo's id argument.
export const APP_LINK_ID_KEYS = [
  'id', 'recordId', 'productId', 'orderId', 'departmentId', 'eventId', 'methodId', 'flowId',
  'itemId', 'setId', 'catalogueId', 'calendarId', 'visitId', 'campaignId', 'taskId', 'listId',
];

export interface ParsedAppLink {
  path: string;
  contactId: string | null;
}

export function parseAppLink(url: string): ParsedAppLink | null {
  const m = url.match(/^([a-z][a-z0-9+.-]*):\/\/([^/?#]*)([^?#]*)(\?[^#]*)?(#.*)?$/i);
  if (!m) return null;
  const scheme = m[1].toLowerCase();
  const host = m[2].toLowerCase();
  let path = m[3] || '';
  const query = m[4] || '';
  const hash = m[5] || '';

  if (scheme === 'https' || scheme === 'http') {
    if (!APP_LINK_HOSTS.includes(host)) return null;
  } else if (APP_SCHEMES.includes(scheme)) {
    // dislix://visit/123 → host "visit", path "/123"
    path = `/${host}${path}`;
  } else {
    return null;
  }

  // Hash-routed web links: https://connect.dislix.com/#/visit/123
  if (hash.startsWith('#/')) path = hash.slice(1);
  path = path.replace(/\/+$/, '') || '/';
  if (IGNORED_PATH_PREFIXES.some((prefix) => path.startsWith(prefix))) return null;

  const contactMatch = query.match(/[?&]contact=([^&]+)/);
  const contactId = contactMatch ? decodeURIComponent(contactMatch[1]) : null;
  if (path === '/' && !contactId) return null;
  return { path, contactId };
}
